import * as React from 'react';

import { cn } from '../../../lib/utils';

// Stadium track: rx = height / 2 rounds both ends into semicircles, so the
// straight sides plus the two half-circle ends form a continuous "track".
// Perimeter = 2 * (width - 2 * rx) + 2 * PI * rx.
const TRACK_WIDTH = 28;
const TRACK_HEIGHT = 12;
const TRACK_RADIUS = TRACK_HEIGHT / 2;
const TRACK_PERIMETER =
  2 * (TRACK_WIDTH - 2 * TRACK_RADIUS) + 2 * Math.PI * TRACK_RADIUS;
const DASH_SEGMENT = TRACK_PERIMETER * 0.26;
const DASH_GAP = TRACK_PERIMETER - DASH_SEGMENT;

export interface RaceTrackLoaderProps extends React.SVGAttributes<SVGSVGElement> {
  /** Accessible label. Omit (default) to render as a decorative, aria-hidden icon. */
  label?: string;
}

/**
 * Indeterminate loading indicator shaped like a race track (a stadium-shaped
 * loop with a segment that travels continuously around it), used in place of
 * a spinning circle. Sizes via the standard `size-*` / `w-*`/`h-*` utility
 * classes on `className`, same as a lucide icon.
 */
export const RaceTrackLoader = React.forwardRef<
  SVGSVGElement,
  RaceTrackLoaderProps
>(function RaceTrackLoader({ className, label, ...props }, ref) {
  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${TRACK_WIDTH + 4} ${TRACK_HEIGHT + 4}`}
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn('size-4 text-muted-foreground', className)}
      {...props}
    >
      <rect
        x={2}
        y={2}
        width={TRACK_WIDTH}
        height={TRACK_HEIGHT}
        rx={TRACK_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        opacity={0.25}
      />
      <rect
        x={2}
        y={2}
        width={TRACK_WIDTH}
        height={TRACK_HEIGHT}
        rx={TRACK_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeDasharray={`${DASH_SEGMENT} ${DASH_GAP}`}
        className="origin-center motion-safe:animate-racetrack-dash motion-reduce:opacity-60"
        style={
          {
            '--racetrack-dash-offset': `${-TRACK_PERIMETER}`,
          } as React.CSSProperties
        }
      />
    </svg>
  );
});
